import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/lib/ws";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Hash, Lock, MessageSquare, Pin, Bell, BellOff, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const SALES_FLOOR = "sales-floor";
const MANAGERS = "managers";

const ChannelIcon = ({ type }) => {
  if (type === "dm") return <MessageSquare className="w-3.5 h-3.5" />;
  if (type === "managers") return <Lock className="w-3.5 h-3.5" />;
  return <Hash className="w-3.5 h-3.5" />;
};

export default function Chat() {
  const { user } = useAuth();
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const initialChannel = params.get("c") || SALES_FLOOR;

  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(initialChannel);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const [showMention, setShowMention] = useState(false);
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [dmTargets, setDmTargets] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const refreshChannels = useCallback(async () => {
    try {
      const r = await api.get("/messages/channels");
      setChannels(r.data);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (id) => {
    try {
      const r = await api.get(`/messages/${id}`);
      setMessages(r.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("No access to this channel");
      setMessages([]);
    }
  }, []);

  useEffect(() => { refreshChannels(); }, [refreshChannels]);
  useEffect(() => { (async () => {
    try { const r = await api.get("/users"); setUsers(r.data.filter(u => u.user_id !== user?.user_id)); } catch {}
  })(); }, [user]);
  useEffect(() => { loadMessages(activeId); setParams({ c: activeId }); }, [activeId, loadMessages, setParams]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useWebSocket((msg) => {
    if (msg.event === "new_message") {
      const { channel_id, message } = msg.payload;
      if (channel_id === activeId) {
        setMessages(m => [...m, message]);
      }
      // Refresh channel list to surface new DMs
      if (channel_id?.startsWith("dm:")) refreshChannels();
    }
    if (msg.event === "message_pinned") {
      const { channel_id, msg_id, pinned } = msg.payload;
      if (channel_id === activeId) {
        setMessages(ms => ms.map(m => m.msg_id === msg_id ? { ...m, pinned } : m));
      }
    }
  });

  const activeChan = channels.find(c => c.channel_id === activeId);
  const isAmbient = activeId === SALES_FLOOR;

  const send = async () => {
    if (!text.trim()) return;
    // Parse @mentions: match @display_name (case-insensitive)
    const mentions = [];
    const mentionMatches = text.matchAll(/@([\w-]+)/g);
    for (const m of mentionMatches) {
      const name = m[1].toLowerCase();
      const u = users.find(x => (x.display_name || x.name).toLowerCase() === name);
      if (u) mentions.push(u.user_id);
    }
    try {
      await api.post(`/messages/${activeId}`, { text, mentions });
      setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const togglePin = async (m) => {
    if (!isManager) return;
    await api.post(`/messages/${m.msg_id}/pin`);
  };

  const toggleMute = async () => {
    const cur = activeChan?.muted;
    await api.post("/messages/mute", { channel_id: SALES_FLOOR, muted: !cur });
    refreshChannels();
    toast.success(cur ? "Unmuted #sales-floor" : "Muted #sales-floor");
  };

  const openDmDialog = async () => {
    const r = await api.get("/messages/dm/targets");
    setDmTargets(r.data);
    setDmDialogOpen(true);
  };

  const startDm = (otherId) => {
    const ids = [user.user_id, otherId].sort();
    const channelId = `dm:${ids[0]}:${ids[1]}`;
    setDmDialogOpen(false);
    setActiveId(channelId);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Build mention suggestions when @ is typed
  const handleTextChange = (val) => {
    setText(val);
    const lastAt = val.lastIndexOf("@");
    const afterAt = lastAt >= 0 ? val.slice(lastAt + 1) : "";
    setShowMention(lastAt >= 0 && !afterAt.includes(" ") && afterAt.length >= 0 && val[lastAt - 1] !== "");
  };

  const insertMention = (u) => {
    const name = u.display_name || u.name;
    const lastAt = text.lastIndexOf("@");
    setText(text.slice(0, lastAt) + `@${name.split(" ")[0]} `);
    setShowMention(false);
    inputRef.current?.focus();
  };

  const pinnedMsgs = messages.filter(m => m.pinned);

  return (
    <Layout>
      <div className="grid grid-cols-12 h-[calc(100vh-3.5rem)]">
        {/* Sidebar */}
        <aside className="col-span-3 border-r border-white/10 bg-[#0A0A0A] flex flex-col">
          <div className="p-4 border-b border-white/10">
            <h2 className="font-display text-2xl font-black uppercase tracking-wider">Messages</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/30 px-2 py-2">Channels</div>
            {channels.filter(c => c.type !== "dm").map(c => (
              <ChannelRow key={c.channel_id} ch={c} active={c.channel_id === activeId} onClick={() => setActiveId(c.channel_id)} />
            ))}
            <div className="flex items-center justify-between px-2 pt-4 pb-2">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">Direct Messages</span>
              {isManager && (
                <Button size="sm" variant="ghost" onClick={openDmDialog} className="h-6 w-6 p-0 text-white/40 hover:text-white" data-testid="new-dm-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            {channels.filter(c => c.type === "dm").map(c => (
              <ChannelRow key={c.channel_id} ch={c} active={c.channel_id === activeId} onClick={() => setActiveId(c.channel_id)} />
            ))}
            {!channels.some(c => c.type === "dm") && (
              <div className="text-xs text-white/30 italic px-2">No direct messages yet</div>
            )}
          </div>
        </aside>

        {/* Main pane */}
        <section className="col-span-9 flex flex-col bg-[#0A0A0A]">
          <header className="px-5 h-14 border-b border-white/10 flex items-center gap-3">
            <ChannelIcon type={activeChan?.type} />
            <span className="font-display text-lg font-black uppercase tracking-wider" data-testid="active-channel-name">
              {activeChan?.label || activeId}
            </span>
            {isAmbient && (
              <span className="text-[10px] uppercase tracking-widest text-white/30 ml-2">· Ambient (no push)</span>
            )}
            {activeId === SALES_FLOOR && (
              <Button size="sm" variant="ghost" onClick={toggleMute} className="ml-auto text-white/50 hover:text-white text-[10px] uppercase tracking-wider rounded-sm" data-testid="mute-toggle-btn">
                {activeChan?.muted ? <><BellOff className="w-3 h-3 mr-1" /> Muted</> : <><Bell className="w-3 h-3 mr-1" /> Notify</>}
              </Button>
            )}
          </header>

          {pinnedMsgs.length > 0 && (
            <div className="border-b border-white/10 bg-[#FFCC00]/5">
              <div className="px-5 py-2 flex items-start gap-3">
                <Pin className="w-3.5 h-3.5 text-[#FFCC00] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-xs text-white/70 truncate">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-[#FFCC00] mr-2">Pinned</span>
                  {pinnedMsgs[0].text}
                  {pinnedMsgs.length > 1 && <span className="text-white/40 ml-2">+{pinnedMsgs.length - 1} more</span>}
                </div>
              </div>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
            {messages.map(m => (
              <MessageRow key={m.msg_id} m={m} mine={m.sender_id === user?.user_id} isManager={isManager} onPin={() => togglePin(m)} />
            ))}
            {!messages.length && (
              <div className="text-white/30 italic text-sm text-center py-8">No messages yet. Say something.</div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/10 p-4 relative">
            {showMention && text.includes("@") && (
              <div className="absolute bottom-full left-4 right-4 bg-[#141414] border border-white/10 max-h-48 overflow-y-auto mb-1 rounded-sm">
                {users.filter(u => {
                  const lastAt = text.lastIndexOf("@");
                  const q = text.slice(lastAt + 1).toLowerCase();
                  return (u.display_name || u.name).toLowerCase().includes(q);
                }).slice(0, 6).map(u => (
                  <button key={u.user_id} onClick={() => insertMention(u)} className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm flex items-center gap-2" data-testid={`mention-${u.user_id}`}>
                    <span className="font-bold">@{(u.display_name || u.name).split(" ")[0]}</span>
                    <span className="text-white/40 text-xs">{u.role.replace("_", " ")}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Textarea
                ref={inputRef}
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Message ${activeChan?.label || activeId}…  (use @ to mention)`}
                className="bg-[#141414] border-white/10 rounded-sm min-h-[44px] max-h-32"
                data-testid="message-input"
              />
              <Button
                onClick={send}
                disabled={!text.trim()}
                className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold self-stretch"
                data-testid="send-message-btn"
              >
                Send
              </Button>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/20 mt-2">Plain text · Enter to send · @ for mentions · 90-day retention</div>
          </div>
        </section>
      </div>

      {/* New DM Dialog */}
      <Dialog open={dmDialogOpen} onOpenChange={setDmDialogOpen}>
        <DialogContent className="bg-[#141414] border-white/10 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider text-xl">New Direct Message</DialogTitle>
            <DialogDescription className="text-white/50">Open a private 1-on-1 thread.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {dmTargets.map(t => (
              <button
                key={t.user_id}
                onClick={() => startDm(t.user_id)}
                className="w-full text-left px-3 py-2.5 bg-[#0A0A0A] border border-white/5 hover:border-white/30 flex items-center gap-3"
                data-testid={`dm-target-${t.user_id}`}
              >
                <div className="w-8 h-8 bg-[#1F1F1F] flex items-center justify-center text-xs font-bold">{t.name.slice(0, 2).toUpperCase()}</div>
                <div className="flex-1">
                  <div className="font-bold uppercase text-sm tracking-wide">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{t.role.replace("_", " ")}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ChannelRow({ ch, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2.5 h-9 text-sm font-bold uppercase tracking-wide rounded-sm ${
        active ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"
      }`}
      data-testid={`channel-${ch.channel_id}`}
    >
      <ChannelIcon type={ch.type} />
      <span className="truncate flex-1 text-left">{ch.label}</span>
      {ch.muted && <BellOff className="w-3 h-3 opacity-50" />}
    </button>
  );
}

function MessageRow({ m, mine, isManager, onPin }) {
  return (
    <div className={`group flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.msg_id}`}>
      <div className="w-8 h-8 bg-[#1F1F1F] flex items-center justify-center text-[10px] font-bold uppercase shrink-0">
        {(m.sender_name || "?").slice(0, 2)}
      </div>
      <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
          <span className="font-bold text-white/70">{m.sender_name}</span>
          <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {m.pinned && <Pin className="w-3 h-3 text-[#FFCC00]" />}
        </div>
        <div className={`px-3 py-2 text-sm leading-relaxed ${mine ? "bg-[#007AFF] text-white" : "bg-[#141414] border border-white/10"} rounded-sm relative`}>
          <MentionText text={m.text} />
          {isManager && (
            <button
              onClick={onPin}
              className="opacity-0 group-hover:opacity-100 absolute -top-2 -right-2 w-6 h-6 bg-[#0A0A0A] border border-white/20 flex items-center justify-center hover:border-[#FFCC00] transition-colors"
              title={m.pinned ? "Unpin" : "Pin"}
              data-testid={`pin-${m.msg_id}`}
            >
              <Pin className={`w-3 h-3 ${m.pinned ? "text-[#FFCC00]" : "text-white/50"}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MentionText({ text }) {
  const parts = text.split(/(@[\w-]+)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("@")
          ? <span key={i} className="font-bold text-[#34C759]">{p}</span>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}
